import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLDClient } from 'launchdarkly-react-client-sdk';

import { AuthContextType, AuthProviderProps } from './AuthContext.types';
import { GetTokenSilentlyOptions, useAuth0 } from '@auth0/auth0-react';
import { axiosInstance, axiosReportingInstance } from 'src/api/axios/axiosInstance';
import { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { signalR } from 'src/signalR';
import { decodeToken, isTokenExpired } from 'src/utils/helpers';
import { getRedirectUri } from 'src/components/AuthWrapper/Auth0Provider';
import { buildHostWithSlug } from 'src/utils/buildHostWithSlug';
import type { FanguardApiContractsUserUserDetailModel } from 'src/api/generated';
import { clientConfig, useClient } from 'src/api/axios/config';
import { queryClient } from 'src/queryClient';
import { getMobileAuthPlugin, getNativeServerUrl, persistNativeSession } from 'src/utils/nativeAuth';

const VITE_API_PATH = import.meta.env.VITE_API_PATH ?? 'access.wicketsoft.com';
const VITE_LOCAL_API_PATH = import.meta.env.VITE_LOCAL_API_PATH ?? 'localhost:5555';
const VITE_LOCAL_API_PATH_REPORTING = import.meta.env.VITE_LOCAL_API_PATH_REPORTING ?? 'localhost:5801';
const IS_LOCAL = import.meta.env.VITE_IS_LOCAL_API === 'true';

const AuthContext = createContext<AuthContextType | null>(null);

interface LocalTokenPayload {
  sub?: string;
  email?: string;
  given_name?: string;
  family_name?: string;
  name?: string;
  sys_admin?: boolean;
  sysadmin?: 'True' | 'False';
  scopes?: string | LocalTokenScope[];
  permissions?: string[];
  'http://www.wicketsoft.com/claims/sys-admin'?: boolean;
}

interface LocalTokenScope {
  client?: string;
  permissions?: string[];
}

const currentUserFromLocalToken = (token: string): FanguardApiContractsUserUserDetailModel | null => {
  try {
    const decoded = decodeToken(token) as LocalTokenPayload;
    const nameParts = decoded.name?.trim().split(/\s+/) ?? [];
    const firstName = decoded.given_name ?? nameParts[0] ?? null;
    const lastName = decoded.family_name ?? (nameParts.length > 1 ? nameParts.slice(1).join(' ') : null);
    const isSysAdmin = decoded.sysadmin?.toLowerCase?.() === 'true' || decoded.sys_admin === true;

    return {
      username: decoded.sub ?? null,
      firstName: firstName ?? null,
      lastName: lastName ?? null,
      email: decoded.email ?? null,
      sysAdmin: isSysAdmin,
    };
  } catch {
    return null;
  }
};

const getTokenScopes = (decodedToken: LocalTokenPayload): LocalTokenScope[] => {
  if (Array.isArray(decodedToken.scopes)) {
    return decodedToken.scopes;
  }
  if (typeof decodedToken.scopes !== 'string' || !decodedToken.scopes.trim()) {
    return [];
  }
  try {
    const scopes = JSON.parse(decodedToken.scopes);
    return Array.isArray(scopes) ? scopes : [];
  } catch (error) {
    console.error('Failed to parse token scopes:', error);
    return [];
  }
};

const getTokenPermissions = (decodedToken: LocalTokenPayload) => {
  const permissions = getTokenScopes(decodedToken)[0]?.permissions ?? decodedToken.permissions ?? [];
  return Array.isArray(permissions)
    ? permissions.filter((permission): permission is string => typeof permission === 'string')
    : [];
};

const currentUserFromAuth0User = (user: {
  sub?: string;
  given_name?: string;
  family_name?: string;
  name?: string;
  email?: string;
  [key: string]: unknown;
}): FanguardApiContractsUserUserDetailModel => {
  return {
    username: user.sub ?? null,
    firstName: user.given_name ?? null,
    lastName: user.family_name ?? null,
    email: user.email ?? null,
    sysAdmin: user['http://www.wicketsoft.com/claims/sys-admin'] === true,
  };
};

const initSignalR = async (token: string) => {
  try {
    const baseUrl = `${axiosInstance.defaults.baseURL}/notification`;
    await signalR.ensureConnection(baseUrl, token);
  } catch (error) {
    console.error('Failed to connesct to SignalR:', error);
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, configAuthData, isNativeWrapper = false }) => {
  const ldClient = useLDClient();
  const {
    user,
    isAuthenticated: auth0IsAuthenticated,
    logout: auth0Logout,
    getAccessTokenSilently,
    isLoading: auth0IsLoading,
    loginWithRedirect,
  } = useAuth0();
  const [isNativeAuthenticating, setIsNativeAuthenticating] = useState(isNativeWrapper);
  const [nativeAuthTrigger, setNativeAuthTrigger] = useState(0);
  const client = useClient();

  const login = (token: string) => {
    try {
      queryClient.clear();
      const decodedToken = decodeToken(token) as LocalTokenPayload;
      const username = decodedToken.sub ?? null;
      const permissions = getTokenPermissions(decodedToken);
      initSignalR(token);
      ldClient?.identify({
        key: `${client}`,
        kind: 'client',
      });
      setAuthData({
        username,
        isAuthenticated: true,
        permissions,
      });
      if (isNativeWrapper || configAuthData?.platform === 'Local') {
        setCurrentUser(currentUserFromLocalToken(token));
      } else if (configAuthData?.platform === 'Auth0') {
        const sysAdmin = decodedToken['http://www.wicketsoft.com/claims/sys-admin'] === true;
        setCurrentUser((prev) => (prev ? { ...prev, sysAdmin } : null));
      }
    } catch (error) {
      console.error('Failed to decode auth token:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      setCurrentUser(null);
      setAuthData({
        isAuthenticated: false,
        username: null,
        permissions: [],
      });
    }
  };

  const [currentUser, setCurrentUser] = useState<FanguardApiContractsUserUserDetailModel | null>(null);

  const [authData, setAuthData] = useState<Omit<AuthContextType, 'login' | 'logout' | 'currentUser'>>(() => {
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');

    if (refreshToken && isTokenExpired(refreshToken)) {
      return { isAuthenticated: false, username: null, permissions: [] };
    }

    if (refreshToken && token && isTokenExpired(token)) {
      return { isAuthenticated: true, username: null, permissions: [] };
    }

    if (!token) {
      return { isAuthenticated: false, username: null, permissions: [] };
    }

    try {
      const decodedToken = decodeToken(token) as LocalTokenPayload;
      const username = decodedToken.sub ?? null;
      initSignalR(token);
      ldClient?.identify({
        key: `${client}`,
        kind: 'client',
      });
      const permissions = getTokenPermissions(decodedToken);
      return {
        isAuthenticated: true,
        username,
        permissions,
      };
    } catch (error) {
      console.error('Failed to decode stored auth token:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      return { isAuthenticated: false, username: null, permissions: [] };
    }
  });

  const addBearerAuth0Token = async (token: string) => {
    const slug = window.location.hostname.split('.')[0]; // cannot rely on client name, needs domain slug for auth0 clients
    const apiHost = buildHostWithSlug(slug, VITE_API_PATH);

    if (IS_LOCAL) {
      axiosInstance.defaults.baseURL = `https://${VITE_LOCAL_API_PATH}`;
      axiosReportingInstance.defaults.baseURL = `https://${VITE_LOCAL_API_PATH_REPORTING}`;
    } else {
      axiosInstance.defaults.baseURL = `${location.protocol}//${apiHost}/api`;
      axiosReportingInstance.defaults.baseURL = `${location.protocol}//${apiHost}/api-reporting`;
    }

    // // Clear existing interceptors to avoid conflicts
    axiosInstance.interceptors.request.clear();
    axiosInstance.interceptors.response.clear();
    axiosReportingInstance.interceptors.request.clear();
    axiosReportingInstance.interceptors.response.clear();

    const trimHeader = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

    const applyAuth0ClientHeader = (config: InternalAxiosRequestConfig) => {
      const explicit = trimHeader(config.headers.get('Client'));
      if (explicit) {
        config.headers.set('Client', explicit);
      } else {
        config.headers.set('Client', configAuthData?.client ?? '');
      }
    };

    axiosInstance.interceptors.request.use(
      (config) => {
        config.headers.set('Authorization', `Bearer-Auth0 ${token}`);
        applyAuth0ClientHeader(config);
        return config;
      },
      (error: AxiosError): Promise<AxiosError> => {
        return Promise.reject(error);
      },
    );
    axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError): Promise<AxiosError> => {
        if (error.response?.status === 401 || error.response?.status === 403) {
          const token = await getAuth0Token(true);
          if (token) {
            error.config?.headers.set('Authorization', `Bearer-Auth0 ${token}`);
          }
        }
        return Promise.reject(error);
      },
    );
    axiosReportingInstance.interceptors.request.use((config) => {
      config.headers.set('Authorization', `Bearer-Auth0 ${token}`);
      applyAuth0ClientHeader(config);
      return config;
    });
    axiosReportingInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError): Promise<AxiosError> => {
        if (error.response?.status === 401 || error.response?.status === 403) {
          const token = await getAuth0Token(true);
          if (token) {
            error.config?.headers.set('Authorization', `Bearer-Auth0 ${token}`);
          }
        }
        return Promise.reject(error);
      },
    );
  };

  const getAuth0Token = async (forceRefresh: boolean = false) => {
    if (isNativeWrapper) {
      const plugin = getMobileAuthPlugin();
      if (!plugin) return;
      try {
        const serverUrl = getNativeServerUrl();
        const session = plugin.refresh ? await plugin.refresh({ serverUrl }) : await plugin.getSession({ serverUrl });
        if (session.authenticated && session.token) {
          persistNativeSession(session);
          await addBearerAuth0Token(session.token);
          login(session.token);
          return session.token;
        }
      } catch (error) {
        console.error('Native token refresh failed:', error);
      }
      return;
    }
    try {
      const options: GetTokenSilentlyOptions = {};
      if (forceRefresh) {
        options.cacheMode = 'off';
      }

      const auth0Token = await getAccessTokenSilently(options);

      await addBearerAuth0Token(auth0Token);
      login(auth0Token);

      return auth0Token;
    } catch (error) {
      console.error(error);
    }
  };

  const logoutFromAuth0 = () => {
    if (isNativeWrapper) {
      getMobileAuthPlugin()
        ?.logout?.({ serverUrl: getNativeServerUrl() })
        .then(() => setNativeAuthTrigger((prev) => prev + 1))
        .catch(console.error);
      return;
    }
    if (auth0IsAuthenticated) {
      auth0Logout({
        logoutParams: {
          returnTo: getRedirectUri(),
        },
      });
    }
  };

  const logout = () => {
    logoutFromAuth0();
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    signalR.stop();
    queryClient.clear();
    setCurrentUser(null);
    setAuthData({
      isAuthenticated: false,
      username: null,
      permissions: [],
    });
  };

  // Derive currentUser from Auth0 id_token profile (user object)
  useEffect(() => {
    if (!isNativeWrapper && configAuthData?.platform === 'Auth0' && auth0IsAuthenticated && user) {
      setCurrentUser(currentUserFromAuth0User(user));
    }
  }, [configAuthData?.platform, auth0IsAuthenticated, isNativeWrapper, user]);

  // Derive currentUser from Local access_token
  useEffect(() => {
    if ((isNativeWrapper || configAuthData?.platform === 'Local') && authData.isAuthenticated) {
      const token = localStorage.getItem('token');
      if (token && !isTokenExpired(token)) {
        setCurrentUser(currentUserFromLocalToken(token));
      }
    }
  }, [authData.isAuthenticated, configAuthData?.platform, isNativeWrapper]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');

    if (refreshToken && token && isTokenExpired(token)) {
      login(token);
    }
  }, []);

  useEffect(() => {
    if (!isNativeWrapper && auth0IsAuthenticated) {
      getAuth0Token();
    }
  }, [user?.sub, auth0IsAuthenticated, isNativeWrapper]);

  useEffect(() => {
    if (!isNativeWrapper || configAuthData?.platform !== 'Auth0') {
      setIsNativeAuthenticating(false);
      return;
    }

    const plugin = getMobileAuthPlugin();
    if (!plugin) {
      setIsNativeAuthenticating(false);
      return;
    }

    let cancelled = false;
    const serverUrl = getNativeServerUrl();
    clientConfig.setPlatform('Native');

    const applySession = async () => {
      setIsNativeAuthenticating(true);
      try {
        const existingToken = localStorage.getItem('token');
        if (existingToken && !isTokenExpired(existingToken)) {
          await addBearerAuth0Token(existingToken);
          login(existingToken);
          return;
        }

        let session = await plugin.getSession({ serverUrl });
        if (!session.authenticated) {
          session = await plugin.login({ serverUrl });
        }
        if (!cancelled && persistNativeSession(session)) {
          clientConfig.setPlatform('Native');
          if (session.client) {
            clientConfig.setClient(session.client);
          }
          await addBearerAuth0Token(session.token);
          login(session.token);
        }
      } catch (error) {
        console.error('Native authentication failed:', error);
      } finally {
        if (!cancelled) {
          setIsNativeAuthenticating(false);
        }
      }
    };

    void applySession();

    return () => {
      cancelled = true;
    };
  }, [configAuthData?.platform, isNativeWrapper, nativeAuthTrigger]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const error = searchParams.get('error');
    const code = searchParams.get('code');
    if (
      configAuthData?.platform === 'Auth0' &&
      !isNativeWrapper &&
      !auth0IsLoading &&
      !authData.isAuthenticated &&
      !auth0IsAuthenticated &&
      !error &&
      !code
    ) {
      loginWithRedirect();
    }
  }, [authData.isAuthenticated, configAuthData?.platform, auth0IsLoading, auth0IsAuthenticated, isNativeWrapper]);

  return (
    <AuthContext.Provider value={{ ...authData, isAuthenticating: isNativeAuthenticating, currentUser, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
